import { VNode } from 'preact';
import ChevronDownIcon from 'images/icons/ChevronDown.svg';
import ChevronUpIcon from 'images/icons/ChevronUp.svg';
import { useCallback, useState } from 'preact/hooks';
import { ExtendedReviewsWidgetSettings } from 'types/settings';
import { RatingDetails } from 'types/widget';
import { toFixed } from 'utilities/content';

import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { Icon } from 'components/Icon';
import { useSettings } from 'components/SettingsProvider';
import { Stack } from 'components/Stack';
import { Stars } from 'components/Stars';
import { Text } from 'components/Text';

import { StarsBreakdown } from '../StarsBreakdown';

interface HeaderProps {
  ratingDetails: RatingDetails | null;
  totalReviews: number;
  overallRating: number;
  actions?: VNode[];
  onFilterClick: (value: keyof RatingDetails) => void;
}

export function Header({
  ratingDetails,
  totalReviews,
  overallRating,
  actions,
  onFilterClick,
}: HeaderProps) {
  const { show_stars_breakdown, collapse_stars_breakdown } =
    useSettings<ExtendedReviewsWidgetSettings>();
  const [detailsCardOpen, setDetailsCardOpen] = useState(
    !collapse_stars_breakdown,
  );

  const handleToggleDetails = useCallback(
    () => setDetailsCardOpen((open) => !open),
    [],
  );

  const actionsMarkup = actions ? (
    <Stack.Item fill classNames={['wbtxReviews__header-actions']}>
      <Stack alignment="center" distribution="equalSpacing">
        {actions}
      </Stack>
    </Stack.Item>
  ) : null;

  const starsBreakdownMarkup = show_stars_breakdown &&
    detailsCardOpen &&
    ratingDetails && (
      <Card
        sectioned
        subdued
        rounded={false}
        classNames={['wbtxReviews__header-details']}
      >
        <StarsBreakdown
          items={ratingDetails}
          totalReviews={totalReviews}
          onClick={onFilterClick}
        />
      </Card>
    );

  return (
    <Stack vertical classNames={['wbtxReviews__header']}>
      <Stack alignment="center" wrap="reverse">
        <Stack.Item stretch>
          <Stack alignment="center" spacing="tight">
            <Stars value={overallRating} />
            <Text
              size="large"
              variation="strong"
              classNames={['wbtxReviews__header-overallRating']}
            >
              {toFixed(overallRating)}
            </Text>
            {show_stars_breakdown && ratingDetails && (
              <Button
                plain
                shadow
                accessibilityLabel={`${
                  detailsCardOpen ? 'Fold' : 'Unfold'
                } rating details`}
                icon={
                  <Icon
                    source={detailsCardOpen ? ChevronUpIcon : ChevronDownIcon}
                  />
                }
                onClick={handleToggleDetails}
              />
            )}
          </Stack>
        </Stack.Item>
        {actionsMarkup}
      </Stack>
      {starsBreakdownMarkup}
    </Stack>
  );
}
